import { useCallback, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Creators as DataCreators } from "model/data"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { createUuid, type Uuid } from "helpers/types/uuid"
import { warn } from "helpers/dev-logger"
import { useAppDispatch, useAppStore } from "hooks/redux"
import type { ReduxState } from "model/state"
import {
  useCreateInstance,
  useReseedInstance,
  useSaveInstanceWorkspace,
  DASHBOARD_INSTANCES_RESOURCE,
} from "resources/dashboard-instances"
import { useCreateLayout } from "resources/layouts"
import { instancesSlice, selectInstanceById } from "../store/instances-slice"
import { createAndReseedLayout } from "../layouts/create-and-reseed-layout"
import { exportLayoutConfig } from "../layouts/export-layout-config"
import { serializeWorkspace, type WorkspaceSerializableState } from "../layouts/serialize-workspace"
import type { GetArrangement } from "./use-workspace-sync"

export interface DuplicateDashboard {
  // Never throws: toasts on success/failure, so the caller only awaits resolution.
  duplicate: (sourceLayoutId: Uuid, sourceName: string) => Promise<void>
  // True while a duplicate is in flight — a slow network + reopen-and-click would otherwise mint a
  // second copy, since the menu action itself has no dialog to disable against. [APP-3309]
  isDuplicating: boolean
}

/**
 * "Duplicate dashboard" (APP-2709).
 *
 * Clones the live dashboard the user is viewing into a NEW instance and routes to it. The copy links
 * to the SAME layout as the source — this is an instance-level duplicate, so no new layout row is
 * written. Its workspace is a snapshot of the source's live state, so instance-only bits (ai-artifact
 * panels, column widths) carry over — unlike "save as new layout", which deliberately drops them.
 *
 * @param dashboardId - The dashboard slot whose live slice state is the duplication source. In the
 *   routed model this equals the active instanceId.
 * @param getArrangement - Reads the current Dockview arrangement blob at call time, so the copy is
 *   WYSIWYG. Returns null (panels-only) when no engine is mounted.
 */
export function useDuplicateDashboard(
  dashboardId: string,
  getArrangement: GetArrangement,
): DuplicateDashboard {
  const store = useAppStore()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const createInstance = useCreateInstance()
  const createLayout = useCreateLayout()
  const reseedInstance = useReseedInstance()
  const saveWorkspace = useSaveInstanceWorkspace()
  const [isDuplicating, setIsDuplicating] = useState(false)
  // isDuplicating flips next render; two synchronous calls in the same tick would both pass a
  // state-only guard. The ref blocks the second in the same tick. [APP-3309]
  const isDuplicatingRef = useRef(false)

  const duplicate = useCallback(
    async (sourceLayoutId: Uuid, sourceName: string) => {
      if (isDuplicatingRef.current) return
      isDuplicatingRef.current = true
      setIsDuplicating(true)
      const copyName = `${sourceName} (copy)`
      // Capture BEFORE any await, or a mid-duplicate account switch routes to the wrong account. [APP-3309]
      const accountAlias = (store.getState() as unknown as ReduxState).global.selected.account
      try {
        // Snapshot live state at call time (not a render closure) so the copy is WYSIWYG.
        const arrangement = getArrangement()
        const state = store.getState() as unknown as WorkspaceSerializableState
        const workspace = serializeWorkspace(state, dashboardId, arrangement)

        // A layout-less source (legacy/never-seeded) self-heals below instead of copying "no layout".
        // Prefer the registry's layoutId over the caller's — it's synced synchronously by reseeds,
        // while sourceLayoutId comes from the HTTP cache, which only invalidates. [APP-3309]
        const registryLayoutId = selectInstanceById(store.getState() as unknown as ReduxState, dashboardId)?.layoutId
        let layoutId = (registryLayoutId ?? sourceLayoutId) as Uuid
        if (!layoutId) {
          const { config } = exportLayoutConfig(state, dashboardId, arrangement)
          const reseeded = await createAndReseedLayout({
            dashboardId, name: sourceName, config, createLayout, reseedInstance,
          })
          // Syncs the registry right away so a retry after a later failure doesn't re-enter self-heal. [APP-3266]
          dispatch(instancesSlice.actions.upsertInstance({
            instanceId: reseeded.instanceId,
            name: reseeded.name,
            layoutId: reseeded.layoutId,
            layoutVersionSeeded: reseeded.layoutVersionSeeded,
            lastOpenedAt: reseeded.lastOpenedAt,
          }))
          layoutId = reseeded.layoutId
        }

        const instanceId = createUuid()
        const created = await createInstance({ instanceId, name: copyName, layoutId })

        // Best-effort: the open-time autosave re-persists on failure. Use created.instanceId
        // (server-echoed), not the client-minted one, or a divergent id strands the copy. [APP-3309]
        try {
          await saveWorkspace(created.instanceId, workspace)
        } catch (error) {
          warn("Duplicate workspace save failed; the open-time autosave will re-persist it", error)
        }

        // Route by instanceId, not the slug — a slug route landing before the cache refetches can
        // misfire the resolver's lazy-migration path. Mirrors useCreateDashboard. [APP-3309]
        if (accountAlias) navigate(`/${accountAlias}/dashboard/${created.instanceId}`)

        dispatch(NotificationCreators.add("success", null, `Duplicated as "${copyName}".`))
      } catch {
        dispatch(NotificationCreators.add("error", null, "Couldn't duplicate the dashboard. Please try again."))
      } finally {
        // One reset regardless of exit path — the cache may predate a self-heal reseed or the copy. [APP-3309]
        dispatch(DataCreators.resetCollections([DASHBOARD_INSTANCES_RESOURCE]))
        isDuplicatingRef.current = false
        setIsDuplicating(false)
      }
    },
    [
      store, dispatch, navigate, createInstance, createLayout, reseedInstance, saveWorkspace, dashboardId,
      getArrangement,
    ],
  )

  return { duplicate, isDuplicating }
}
